import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, FileSpreadsheet, FileText, Loader2, Plus } from "lucide-react";
import SupplierOrders from "@/components/SupplierOrders";
import { SortableTh, useTableSort, sortRows } from "@/components/SortableTh";
import { ProductStatus } from "@/components/ProductStatus";

export default function SupplierDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { canWrite, subscriptionDisabled } = useAuth();
  const tab = sp.get("tab") === "ordini" ? "ordini" : "prodotti";
  const setTab = (t) => setSp(t === "ordini" ? { tab: "ordini" } : {}, { replace: true });

  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const { sortField, sortDir, toggleSort } = useTableSort();
  const sortedProducts = useMemo(() => sortRows(products, sortField, sortDir, {
    code: p => p.code,
    name: p => p.name,
    supplier_role: p => p.supplier_role,
    supplier_product_name: p => p.supplier_product_name,
    supplier_raw_material_code: p => p.supplier_raw_material_code,
    supplier_format: p => p.supplier_format,
    supplier_order_udm: p => p.supplier_order_udm,
  }), [products, sortField, sortDir]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, p] = await Promise.all([
          api.get(`/suppliers/${id}`),
          api.get(`/suppliers/${id}/products`),
        ]);
        setSupplier(s.data);
        setProducts(p.data);
      } catch {
        toast.error("Fornitore non trovato");
        nav("/suppliers");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [id]);

  const exportProducts = async (format) => {
    if (!products.length) { toast.error("Nessun prodotto associato da esportare"); return; }
    setExporting(format);
    try {
      const res = await api.get(`/suppliers/${id}/export?format=${format}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `fornitore_prodotti.${format}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = fname;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    } catch { toast.error("Errore esportazione"); }
    finally { setExporting(null); }
  };

  if (loading) return <div className="p-4 pt-16 md:p-8 lg:p-12 text-sm text-[#707070]">Caricamento...</div>;
  if (!supplier) return null;

  return (
    <div className="workflow-ux p-4 pt-16 md:p-8 lg:p-12" data-testid="supplier-detail-page">
      <button onClick={() => nav("/suppliers")}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-luxury text-[#707070] uppercase hover:text-[#1A1A1A] mb-4"
              data-testid="supplier-detail-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Torna ai fornitori
      </button>
      <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Dettaglio Fornitore</div>
      <h1 className="font-serif text-3xl md:text-4xl mb-6">{supplier.company_name}</h1>

      <div className="flex border-b border-[#E5E5E0] mb-8">
        <button onClick={() => setTab("prodotti")} className={`tab-btn ${tab === "prodotti" ? "active" : ""}`} data-testid="supplier-tab-prodotti">Prodotti</button>
        <button onClick={() => setTab("ordini")} className={`tab-btn ${tab === "ordini" ? "active" : ""}`} data-testid="supplier-tab-ordini">Ordini</button>
      </div>

      <div className="luxury-card p-4 md:p-8">
        {tab === "ordini" ? (
          <SupplierOrders supplier={supplier} canWrite={canWrite} subscriptionDisabled={subscriptionDisabled} />
        ) : (
          <>
            <div className="flex flex-wrap justify-end gap-3 mb-4">
              {canWrite && (
                <button onClick={() => setSp({ tab: "ordini", new: "1" })}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-[#1A1A1A] text-white hover:bg-[#000] transition"
                        data-testid="supplier-make-order-btn">
                  <Plus className="w-3.5 h-3.5" /> Fai ordine
                </button>
              )}
              {!subscriptionDisabled && <button onClick={() => exportProducts("xlsx")} disabled={!products.length || exporting}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[#E5E5E0] hover:border-[#C5B39B] disabled:opacity-40 transition"
                      data-testid="supplier-modal-export-xlsx">
                {exporting === "xlsx" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Esporta XLSX
              </button>}
              {!subscriptionDisabled && <button onClick={() => exportProducts("csv")} disabled={!products.length || exporting}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[#E5E5E0] hover:border-[#C5B39B] disabled:opacity-40 transition"
                      data-testid="supplier-modal-export-csv">
                {exporting === "csv" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} CSV
              </button>}
            </div>
            {products.length === 0 ? (
              <div className="text-sm text-[#707070]">Nessun prodotto associato a questo fornitore.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E5E0] text-left text-[10px] tracking-luxury text-[#707070] uppercase">
                      <SortableTh field="code" label="Codice prodotto" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
                      <SortableTh field="name" label="Prodotto" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
                      <SortableTh field="supplier_role" label="Tipo" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
                      <SortableTh field="supplier_product_name" label="Nome prodotto Fornitore" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
                      <SortableTh field="supplier_raw_material_code" label="Codice prodotto Fornitore" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
                      <SortableTh field="supplier_format" label="Formato" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
                      <SortableTh field="supplier_order_udm" label="UDM" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProducts.map(p => {
                      const associationId = p.association_id || `${p.id}__${p.supplier_role || "raw_material"}`;
                      return (
                      <tr key={associationId} className="border-b border-[#F0F0EA]" data-testid={`supplier-product-${associationId}`}>
                        <td className="px-3 py-2">
                          <button onClick={() => nav(`/products/${p.id}`)}
                                  className="text-[#1A1A1A] font-mono hover:text-[#C5B39B] hover:underline transition"
                                  data-testid={`supplier-product-code-link-${associationId}`}>
                            {p.code || "—"}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => nav(`/products/${p.id}`)}
                                  className="text-[#1A1A1A] text-left hover:text-[#C5B39B] hover:underline transition"
                                  data-testid={`supplier-product-name-link-${associationId}`}>
                            {p.name}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <ProductStatus tone={p.supplier_role === "packaging" ? "info" : "success"}
                                         testId={`supplier-product-role-${associationId}`}>
                            {p.supplier_role === "packaging" ? "Packaging" : "Materia Prima"}
                          </ProductStatus>
                        </td>
                        <td className="px-3 py-2 text-[#707070]">{p.supplier_product_name || "—"}</td>
                        <td className="px-3 py-2 text-[#707070]">{p.supplier_raw_material_code || "—"}</td>
                        <td className="px-3 py-2 text-[#707070]">{p.supplier_format || "—"}</td>
                        <td className="px-3 py-2 text-[#707070] capitalize">{p.supplier_order_udm || "—"}</td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
